import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AssociatedFranchise, FranchiseIntel } from "../../interfaces/establishments";
import { FRENCH_DEPARTMENTS } from "../../lib/frenchCities";
import { FranchiseLogo } from "./FranchiseLogo";

const DEPARTMENT_NAME = new Map(FRENCH_DEPARTMENTS.map((d) => [d.code, d.name]));

function pct(part: number, whole: number): string {
    return whole > 0 ? `${Math.round((part / whole) * 100)} %` : "—";
}

/* ─────────────────────────── Panel shell ─────────────────────────── */

export function IntelPanel({ eyebrow, title, aside, children, className = "" }: {
    eyebrow: string;
    title: string;
    aside?: ReactNode;
    children: ReactNode;
    className?: string;
}) {
    return (
        <section className={`flex flex-col rounded-2xl border border-border bg-surface ${className}`}>
            <header className="flex items-end justify-between gap-4 px-6 pt-5 pb-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{eyebrow}</p>
                    <h2 className="mt-1 font-display text-xl font-normal leading-snug text-ink">{title}</h2>
                </div>
                {aside}
            </header>
            {children}
        </section>
    );
}

/* ─────────────────────────── KPI strip ─────────────────────────── */

export function IntelKpis({ intel }: { intel: FranchiseIntel }) {
    const items = [
        { label: "Sites actifs", value: intel.activeSites, note: intel.totalSites > intel.activeSites ? `${intel.totalSites - intel.activeSites} fermés` : "réseau entier" },
        { label: "Franchisés identifiés", value: intel.franchisees, note: "opérateurs distincts" },
        { label: "Multi-unités", value: intel.multiUnit, note: `${pct(intel.multiUnit, intel.franchisees)} des franchisés` },
        { label: "Multi-enseignes", value: intel.multiBrand, note: `${pct(intel.multiBrand, intel.franchisees)} opèrent une autre marque` },
        { label: "Départements", value: intel.departments.length, note: "couverts par le réseau" },
    ];
    return (
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-5">
            {items.map((k) => (
                <div key={k.label} className="space-y-1 rounded-2xl border border-border bg-surface p-6 last:col-span-2 md:last:col-span-1">
                    <dt className="font-sans text-sm text-ink-muted">{k.label}</dt>
                    <dd className="font-sans text-3xl font-semibold tracking-tight text-ink">
                        {k.value.toLocaleString("fr-FR")}
                    </dd>
                    <dd className="text-xs text-ink-faint">{k.note}</dd>
                </div>
            ))}
        </dl>
    );
}

/* ─────────────────────────── Geography ─────────────────────────── */

export function DepartmentBars({ intel, limit = 8 }: { intel: FranchiseIntel; limit?: number }) {
    const rows = intel.departments.slice(0, limit);
    const max = rows[0]?.count ?? 0;
    const rest = intel.departments.slice(limit).reduce((s, d) => s + d.count, 0);

    if (rows.length === 0) {
        return <p className="px-6 pb-6 text-sm text-ink-faint">Aucune adresse exploitable.</p>;
    }
    return (
        <div className="space-y-3 px-6 pb-6">
            {rows.map((d) => (
                <div key={d.code}>
                    <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                        <span className="truncate text-ink">
                            <span className="mr-2 font-mono text-xs text-ink-faint">{d.code}</span>
                            {DEPARTMENT_NAME.get(d.code) ?? "—"}
                        </span>
                        <span className="font-mono text-sm text-ink">{d.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-surface-sunken">
                        <div className="h-1.5 rounded-full bg-accent" style={{ width: `${(d.count / max) * 100}%` }} />
                    </div>
                </div>
            ))}
            {rest > 0 && (
                <p className="pt-1 text-xs text-ink-faint">
                    + {rest} site{rest > 1 ? "s" : ""} dans {intel.departments.length - rows.length} autre{intel.departments.length - rows.length > 1 ? "s" : ""} département{intel.departments.length - rows.length > 1 ? "s" : ""}
                </p>
            )}
        </div>
    );
}

/* ─────────────────────────── Growth ─────────────────────────── */

export function OpeningsChart({ intel }: { intel: FranchiseIntel }) {
    const data = intel.openingsByYear;
    const max = Math.max(1, ...data.map((d) => d.count));
    const total = data.reduce((s, d) => s + d.count, 0);
    const last = data[data.length - 1];
    const previous = data[data.length - 2];

    return (
        <div className="px-6 pb-6">
            <div className="mb-4 flex items-baseline gap-2">
                <span className="font-mono text-3xl font-medium tracking-tight text-ink">{total}</span>
                <span className="text-sm text-ink-muted">ouvertures datées sur {data.length} ans</span>
            </div>
            <div className="flex h-28 items-end gap-2">
                {data.map((d, i) => (
                    <div key={d.year} className="flex h-full flex-1 flex-col items-center justify-end gap-1" title={`${d.year} : ${d.count}`}>
                        <span className="font-mono text-xs text-ink-muted">{d.count || ""}</span>
                        <div
                            className={`w-full rounded-sm ${i === data.length - 1 ? "bg-accent" : "bg-ink/80"}`}
                            style={{ height: `${Math.max(d.count > 0 ? 4 : 1, (d.count / max) * 100)}%` }}
                        />
                    </div>
                ))}
            </div>
            <div className="mt-2 flex gap-2">
                {data.map((d) => (
                    <span key={d.year} className="flex-1 text-center font-mono text-xs text-ink-faint">
                        {String(d.year).slice(2)}
                    </span>
                ))}
            </div>
            {last && previous && (
                <p className="mt-4 text-xs text-ink-muted">
                    {last.year} en cours : <span className="font-medium text-ink">{last.count}</span> ouverture{last.count > 1 ? "s" : ""}, contre {previous.count} en {previous.year}.
                </p>
            )}
        </div>
    );
}

/* ─────────────────────────── Associated brands ─────────────────────────── */

export function AssociatedBrands({ associated, franchisees }: { associated: AssociatedFranchise[]; franchisees: number }) {
    const navigate = useNavigate();
    if (associated.length === 0) {
        return <p className="px-6 pb-6 text-sm text-ink-faint">Aucun recoupement significatif avec une autre enseigne.</p>;
    }
    const max = associated[0].sharedFranchisees;
    return (
        <div className="pb-3">
            {associated.map((a) => (
                <button
                    key={a._id}
                    type="button"
                    onClick={() => navigate(`/franchiseurs/${a._id}`)}
                    className="group flex w-full items-center gap-3 px-6 py-2.5 text-left transition-colors duration-150 hover:bg-surface-sunken/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                >
                    <FranchiseLogo f={a} size={32} />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-sm font-medium text-ink group-hover:underline">{a.name}</span>
                            <span className="font-mono text-sm text-ink">{a.sharedFranchisees}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                            <div className="h-1 flex-1 rounded-full bg-surface-sunken">
                                <div className="h-1 rounded-full bg-ink/70" style={{ width: `${(a.sharedFranchisees / max) * 100}%` }} />
                            </div>
                            <span className="w-10 text-right font-mono text-xs text-ink-faint">{pct(a.sharedFranchisees, franchisees)}</span>
                        </div>
                    </div>
                </button>
            ))}
        </div>
    );
}
